import React, { useState } from 'react';
import { Form, Input, Button, Select } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthShell from '../AuthShell/AuthShell';
import { UserIcon, ChevronIcon, MailIcon, ArrowLeftIcon } from '../AuthShell/icons';
import { userKeyRules, isKeyNotOnRecord, NoEmailOnRecord } from '../AuthShell/userKey';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import authService, { FORGOT_PASSWORD_NO_EMAIL } from '../../services/authService';

const RequiredLabel = ({ text }: { text: string }) => (
  <span>{text} <span className="login-required">*</span></span>
);

/**
 * Step 1 of the password reset flow.
 * Collects E-Code/email + company and asks the backend to mail a reset link.
 */
const ForgotPassword = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyData, companyLocked, resolvingCompany, unknownKey, setInputCode } = useCompanyOptions(form);

  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [sentTo, setSentTo] = useState<string | null>(null);

  /** E Code or email the backend has confirmed matches no active user. */
  const keyNotOnRecord = isKeyNotOnRecord(username.trim(), unknownKey);

  const handleUsernameChange = (value: string) => {
    setUsername(value);
    setInputCode(value);
  };

  /** Back to login, preserving ?redirectUrl so the journey is not lost. */
  const backToLogin = () => {
    const search = searchParams.toString();
    navigate(search ? `/?${search}` : '/');
  };

  const handleSubmit = async (values: any) => {
    // No account behind this key — the inline notice already tells the user to
    // get the email ID updated, so don't spend a round trip on it.
    if (keyNotOnRecord) return;

    setLoading(true);
    setErrorMsg('');
    try {
      await authService.forgotPassword({
        username: values.username,
        company_id: values.company_id,
      });
      setSentTo(values.username);
    } catch (error: any) {
      setErrorMsg(
        error?.code === FORGOT_PASSWORD_NO_EMAIL
          ? 'No email ID is registered for this account. Please update your email ID to proceed with the forgot password.'
          : error.message || 'Unable to send the reset link. Please try again.',
      );
    } finally {
      setLoading(false);
    }
  };

  /* ------------------------------ Mail sent ------------------------------ */
  if (sentTo) {
    return (
      <AuthShell tagline="Password reset">
        <div className="login-state">
          <div className="login-state-icon"><MailIcon /></div>
          <div className="login-state-title">Check your inbox</div>
          <div className="login-state-text">
            If <strong>{sentTo}</strong> matches an active account, we have mailed a password
            reset link to the registered email address. The link expires in 30 minutes.
          </div>
        </div>
        <Button className="login-submit-btn" type="primary" onClick={backToLogin}>
          Back to sign in
        </Button>
        <div className="login-state-foot">
          Didn&apos;t receive it?{' '}
          <button type="button" className="login-link" onClick={() => setSentTo(null)}>
            Try again
          </button>
        </div>
      </AuthShell>
    );
  }

  /* -------------------------------- Form --------------------------------- */
  return (
    <AuthShell tagline="Forgot your password? Enter your details and we'll mail you a reset link.">
      <Form
        form={form} name="forgot-password" layout="vertical" className="login-form"
        onFinish={handleSubmit} autoComplete="off"
      >
        {errorMsg ? <div className="login-alert">{errorMsg}</div> : null}

        <Form.Item
          label={<RequiredLabel text="E Code/ Email" />} name="username"
          rules={userKeyRules}
        >
          <Input
            prefix={<UserIcon />} placeholder="Enter your E Code/Email"
            onChange={(e) => handleUsernameChange(e?.target?.value ?? '')}
          />
        </Form.Item>
        {keyNotOnRecord ? <NoEmailOnRecord /> : null}

        <Form.Item
          label={<RequiredLabel text="Company" />} name="company_id"
          className="login-select-field"
          rules={[{ required: true, message: 'Company is required' }]}
        >
          <Select
            showSearch placeholder="Select Company"
            optionFilterProp="children"
            suffixIcon={<ChevronIcon />}
            disabled={companyLocked}
            loading={resolvingCompany}
            filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
            options={companyData?.map((item: any) => ({ value: item.id, label: item.name }))}
          />
        </Form.Item>
        {companyLocked ? (
          <div className="login-field-hint">Auto-selected from your E Code/Email</div>
        ) : null}

        <Button
          type="primary" htmlType="submit"
          loading={loading} className="login-submit-btn login-submit-btn--spaced"
        >
          Send reset link
        </Button>

        <button type="button" className="login-back-link" onClick={backToLogin}>
          <ArrowLeftIcon /> Back to sign in
        </button>
      </Form>
    </AuthShell>
  );
};

export default ForgotPassword;
